import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type JSX, type MouseEvent } from 'react';
import { getAuthInstance, signOutTaki } from '@taki/auth';
import type { TakiConfig } from '@taki/runtime-config';
import {
  Avatar, AvatarLia, IconAI, IconCheck, IconChevronDown, IconClose, IconModule, IconPen1,
  IconMagnifyingGlass, IconNewFile, IconViewDocumentFiles,
  BarreDeSaisie, CartesDeFichesLia, EtatDeLiaAuTravail, announceExternalRecordMutation, cleDeSujetLia, cn, resumeDesOutilsLia, useBrouillon, useIaSettingsPort, useMagasinDeBrouillons,
  type EntreeDeMention,
} from '@taki/ui';
import type { ShellAppEntry } from '../types.js';
import { LiaHead } from '../surfaces/LiaLauncher.js';
import type { LiaUnread } from '../surfaces/LiaLauncher.js';
import { askTaki, type AskContext, type AskLine, type PlaceLink, type RecordLink, type ToolStep } from '../surfaces/askTakiClient.js';
import { AskTakiError } from '../surfaces/askTakiError.js';
import { announceCompletedMutations } from '../surfaces/askTakiMutations.js';
import { MessageText } from '../surfaces/messageText.js';
import { askTakiHistory } from '../surfaces/askTakiHistory.js';
import { liaUnread } from '../surfaces/liaUnread.js';
import { recoveryCheckpoint, type Recovery, type Resume } from '../surfaces/liaRecovery.js';
import { liaKnownRecords, liaRecordCards, liaUnresolvedFailure } from '../surfaces/liaRecordCards.js';
import { useScrollShadows } from '../useScrollShadows.js';
import { ficheDuComposeur, type FicheSuivie } from './contexte.js';
import { heureDuTour, separateurDeJour, titreDuSujetPourLaNotification, type TourLia } from './sujetsLia.js';

/**
 * LA CONVERSATION AVEC LIA, UN SUJET A LA FOIS (chantier `centre-communication`).
 *
 * C'est le moteur de l'ancienne fenetre flottante (`AskTakiAI.tsx`), repris tel
 * quel pour ce qui tient la conversation (envoi, flux, pause et reprise,
 * recuperation apres coupure, cartes de fiches), et change pour deux choses :
 *
 * - le SUJET est une donnee du parent (`sujet`, `onSujetChange`) : les tours
 *   vivent en base, un document par sujet, et c'est le centre qui les ecrit ;
 *   ici ne reste en memoire de session que ce qui ne survit pas a la page
 *   (la requete en cours, de quoi la recuperer) ;
 * - le FIL est PLAT, comme dans Canaux et comme la maquette hub-a : avatar,
 *   nom, heure, puis le texte, separes par des filets (G9). Plus de bulle.
 *
 * Le composant est monte avec `key={sujet.id}`, et le centre le GARDE monte
 * (cache) tant que la reponse de son sujet tourne ou n'a pas ete lue
 * (`liaEnCours.ts`) : on peut ouvrir une autre conversation, la requete va a
 * son terme et se range dans son sujet. Seul un demontage force (sujet
 * supprime, session changee) coupe la requete, qui reste recuperable.
 */
/**
 * P1 du 06/10/2026 : on ne cite personne dans une conversation avec Lia. Le
 * champ commun ne propose aucune entree, donc `@` reste une lettre : ni liste,
 * ni pastille, ni jeton `@[personId]`. Citer quelqu'un se fait dans un canal,
 * un direct ou un chatter, ou Lia est une participante parmi d'autres. Le
 * serveur ignore de son cote une reference qui arriverait quand meme
 * (`sansPersonnesCitees`, `functions/src/ia/agent.ts`).
 */
const AUCUNE_MENTION: readonly EntreeDeMention[] = [];

export interface SujetEnCours {
  id: string;
  titre: string;
  conversationId: string;
  tours: TourLia[];
  fiche: FicheSuivie | null;
}

export interface ConversationLiaProps {
  sujet: SujetEnCours;
  /** Le parent recoit chaque changement du sujet (tours, fiche suivie) et le persiste. */
  onSujetChange: (mise: (sujet: SujetEnCours) => SujetEnCours, raison: 'tour' | 'fiche' | 'envoi') => Promise<void>;
  config: TakiConfig;
  /** Le contexte de l'ecran, SANS la fiche : la conversation y ajoute la fiche qu'elle suit. */
  contexte: AskContext;
  ficheOuverte: FicheSuivie | null;
  appEntries: readonly ShellAppEntry[];
  onOpenRecord: (record: { id: string; title: string; appId: string; doorId: string }) => void;
  onOpenPlace: (place: { appId: string; doorId?: string }) => void;
  /** Le centre est ouvert : une reponse arrivee est lue (sinon elle allume la mascotte). */
  open: boolean;
  /** Densite du panneau compact (avatars de 1,5rem, texte de 0,8125rem). */
  compact: boolean;
  moi: { nom: string; initiales: string; photoURL?: string | null };
  onEtat: (etat: { pending: boolean; unread: LiaUnread }) => void;
  /** La memoire de session de la requete en cours : une cle par sujet et par compte. */
  storageKey: string;
  /** Un `uid` pour verifier, au demontage, que la session est encore la meme. */
  uid: string;
  /** Le sujet ne s'ecrit plus (archive) : la raison tient lieu de placeholder (G16). */
  lectureSeule?: string | null;
  /**
   * La boite interne est branchee : chaque reponse demande « Reponse de Lia
   * prete » au serveur. Si la personne regarde ce sujet a l'arrivee, le centre
   * la marque lue aussitot ; sinon elle allume la cloche et le rail.
   */
  notifier?: boolean;
}

type Step = ToolStep;
// `draft` n'est plus ecrit ici (le brouillon vit dans le magasin du socle) ; il n'est lu que pour la migration.
type Saved = { draft: string; resume?: Resume; recovery?: Recovery; steps?: Step[]; error?: string; pending?: boolean };

function load(key: string): Saved {
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (saved && typeof saved === 'object') {
      const wasPending = 'pending' in saved && saved.pending === true;
      return {
        draft: wasPending ? '' : 'draft' in saved && typeof saved.draft === 'string' ? saved.draft : '',
        resume: 'resume' in saved && saved.resume && typeof saved.resume === 'object' ? saved.resume as Resume : undefined,
        recovery: 'recovery' in saved && saved.recovery && typeof saved.recovery === 'object' ? saved.recovery as Recovery : undefined,
        steps: 'steps' in saved && Array.isArray(saved.steps) ? saved.steps as Step[] : [],
        error: wasPending ? 'La connexion a été interrompue. Les actions déjà lancées peuvent se terminer.'
          : 'error' in saved && typeof saved.error === 'string' ? saved.error : undefined,
      };
    }
  } catch { /* Stockage de session indisponible : la conversation vit sans lui. */ }
  return { draft: '' };
}

function ToolIcon({ kind, size = 15 }: { kind: Step['kind']; size?: number }): JSX.Element {
  if (kind === 'search') return <IconMagnifyingGlass size={size} />;
  if (kind === 'create') return <IconNewFile size={size} />;
  if (kind === 'update') return <IconPen1 size={size} />;
  if (kind === 'discover') return <IconModule size={size} />;
  if (kind === 'studio') return <IconAI size={size} />;
  return <IconViewDocumentFiles size={size} />;
}

export function ConversationLia({ sujet, onSujetChange, config, contexte, ficheOuverte, appEntries, onOpenRecord, onOpenPlace, open, compact, moi, onEtat, storageKey, uid, lectureSeule = null, notifier = false }: ConversationLiaProps): JSX.Element {
  const initial = useRef<Saved>(load(storageKey));
  const turns = sujet.tours;
  const cleDuBrouillon = cleDeSujetLia(sujet.id);
  const [draft, setDraft] = useBrouillon(cleDuBrouillon);
  const magasinDeBrouillons = useMagasinDeBrouillons();
  const [resume, setResume] = useState<Resume | undefined>(initial.current.resume);
  const [recovery, setRecovery] = useState<Recovery | undefined>(initial.current.recovery);
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState('');
  const [steps, setSteps] = useState<Step[]>(initial.current.steps ?? []);
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<string | null>(initial.current.error ?? null);
  const [reconnect, setReconnect] = useState(false);
  // Le debut de la reponse en cours : le compteur de la bulle (`EtatDeLiaAuTravail`) en part.
  const [debut, setDebut] = useState(() => new Date());
  const [unread, setUnread] = useState<LiaUnread>(initial.current.error ? 'error' : null);
  const openRef = useRef(open);
  openRef.current = open;
  const controller = useRef<AbortController | null>(null);
  const stoppedRequests = useRef(new Set<string>());
  const announcedMutations = useRef(new Set<string>());
  const inflight = useRef<{ requestId: string; context: AskContext; turns: TourLia[]; steps: Step[]; messages: AskLine[]; turnId?: string; resumeId?: string } | null>(null);
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  // Le champ commun a mentions (R2) porte sa propre textarea : on la retrouve pour le focus.
  const champ = useRef<HTMLDivElement>(null);
  const shadows = useScrollShadows();
  const scrollArea = shadows.scrollRef;
  const userScrolled = useRef(false);
  const settings = useIaSettingsPort();
  const unavailable = (settings?.ready === true && !settings.enabled) || lectureSeule !== null;
  const [recordNotice, setRecordNotice] = useState<string | null>(null);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  useEffect(() => { onEtat({ pending, unread }); }, [pending, unread, onEtat]);
  // Demonte, ce sujet ne tient plus rien : le centre l'oublie (la mascotte ne garde pas une reponse qui n'existe plus).
  useEffect(() => () => onEtat({ pending: false, unread: null }), [onEtat]);
  useEffect(() => { if (open) setUnread(null); }, [open]);

  const openRecord = useCallback((record: RecordLink): void => {
    const check = settings?.recordStatus;
    if (!check || !record.family) { setRecordNotice(null); onOpenRecord(record); return; }
    void check(record.family, record.id).then((status) => {
      if (status === 'deleted' || status === 'inaccessible' || status === 'missing') {
        setRecordNotice(`« ${record.title} » : fiche ${status === 'deleted' ? 'supprimée' : status === 'missing' ? 'introuvable' : 'inaccessible avec vos droits'}, elle ne peut pas s’ouvrir.`);
        return;
      }
      setRecordNotice(null);
      onOpenRecord(record);
    });
  }, [settings?.recordStatus, onOpenRecord]);

  // La barre de saisie du socle dimensionne son champ seule : ici on ne fait que retrouver la textarea (focus).
  useLayoutEffect(() => {
    textarea.current = champ.current?.querySelector('textarea') ?? null;
  }, [open]);

  // MIGRATION : le brouillon d'avant le magasin vivait dans la session ; on le reprend une fois, sans ecraser.
  useEffect(() => {
    const ancien = initial.current.draft;
    if (ancien.trim() !== '' && magasinDeBrouillons.lire(cleDuBrouillon) === '') magasinDeBrouillons.ecrire(cleDuBrouillon, ancien);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify({ resume, recovery, steps, error, pending })); }
    catch { /* Stockage de session optionnel. */ }
  }, [storageKey, resume, recovery, steps, error, pending]);
  useEffect(() => { if (open) textarea.current?.focus(); }, [open]);
  useEffect(() => {
    if (open && !userScrolled.current && scrollArea.current) scrollArea.current.scrollTop = scrollArea.current.scrollHeight;
  }, [open, turns, steps, partial, status, error, scrollArea]);
  useEffect(() => () => {
    controller.current?.abort();
    const request = inflight.current;
    if (request && getAuthInstance()?.currentUser?.uid === uid) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          recovery: { requestId: request.requestId, context: request.context, messages: request.messages,
            turnId: request.turnId, resumeId: request.resumeId }, steps: request.steps,
          error: 'Le changement de sujet a interrompu la connexion. Les actions déjà lancées peuvent se terminer.' }));
      } catch { /* Stockage de session optionnel. */ }
    }
  }, [storageKey, uid]);

  const contexteDeLaFiche = (fiche: FicheSuivie | null): AskContext => fiche === null ? contexte
    : { ...contexte, appId: fiche.appId, doorId: fiche.doorId, recordId: fiche.recordId };

  function stop(event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    const request = inflight.current;
    if (request) {
      stoppedRequests.current.add(request.requestId);
      setRecovery({ requestId: request.requestId, context: request.context, messages: request.messages,
        turnId: request.turnId, resumeId: request.resumeId });
    }
    controller.current?.abort();
    controller.current = null;
    inflight.current = null;
    setPending(false);
    setStatus('');
    setPartial('');
    setError('Réponse interrompue. Les fiches déjà créées restent ci-dessus. Récupérez la réponse pour connaître la suite.');
    if (openRef.current) requestAnimationFrame(() => textarea.current?.focus());
  }

  async function submit(event?: FormEvent<HTMLFormElement>): Promise<void> {
    event?.preventDefault();
    const question = draft.trim();
    if (!question || pending || unavailable) return;
    const requestId = crypto.randomUUID();
    // Au premier message, le sujet se met a SUIVRE la fiche ouverte : il la garde ensuite d'un ecran a l'autre.
    const fiche = sujet.fiche ?? ficheOuverte;
    const requestContext: AskContext = contexteDeLaFiche(fiche);
    const interrupted: TourLia[] = error && (steps.length > 0 || recovery)
      ? [{ id: crypto.randomUUID(), role: 'assistant', text: error, steps, at: Date.now() }] : [];
    const next: TourLia[] = [...turns, ...interrupted, { id: requestId, role: 'user', text: question, at: Date.now() }];
    const sauvegarde = onSujetChange((current) => ({ ...current, tours: next, fiche }), 'envoi');
    userScrolled.current = false;
    setResume(undefined);
    setRecovery(undefined);
    setDraft('');
    await runRequest(requestId, requestContext, next, sauvegarde);
  }

  async function continueRequest(): Promise<void> {
    if (pending || unavailable) return;
    const checkpoint = recoveryCheckpoint(resume, recovery);
    const lostConnection = recovery;
    if (!checkpoint && !lostConnection) return;
    setResume(undefined);
    setRecovery(undefined);
    await runRequest(
      checkpoint?.requestId ?? lostConnection!.requestId,
      checkpoint?.context ?? lostConnection!.context,
      turns,
      onSujetChange((current) => current, 'envoi'),
      checkpoint,
      Boolean(lostConnection),
      checkpoint?.messages ?? lostConnection?.messages,
    );
  }

  async function runRequest(requestId: string, requestContext: AskContext, historyTurns: TourLia[], sauvegarde: Promise<void>, checkpoint?: Resume, recovering = false, originalMessages?: AskLine[]): Promise<void> {
    stoppedRequests.current.delete(requestId);
    const history = askTakiHistory(historyTurns.filter((turn) => turn.id !== checkpoint?.turnId), originalMessages);
    setError(null);
    setReconnect(false);
    setPending(true);
    setDebut(new Date());
    setStatus('Lia prépare sa réponse…');
    setRecovery({ requestId, context: requestContext, messages: history, turnId: checkpoint?.turnId, resumeId: checkpoint?.id });
    const earlier = checkpoint ? historyTurns.find((turn) => turn.id === checkpoint.turnId)?.steps ?? [] : recovering ? steps : [];
    setSteps(earlier);
    setPartial('');
    const abort = new AbortController();
    controller.current = abort;
    let activeSteps: Step[] = earlier;
    inflight.current = { requestId, context: requestContext, turns: historyTurns, steps: activeSteps,
      messages: history, turnId: checkpoint?.turnId, resumeId: checkpoint?.id };
    try {
      // Attendre l'ecriture deja lancee par le parent, sans seconde transaction concurrente.
      await sauvegarde;
      if (abort.signal.aborted || stoppedRequests.current.has(requestId) || controller.current !== abort) return;
      const reply = await askTaki(config, sujet.conversationId, requestId, history, requestContext, (chunk) => {
        if (abort.signal.aborted || stoppedRequests.current.has(requestId) || controller.current !== abort) return;
        if (chunk.type === 'status') setStatus(chunk.label);
        if (chunk.type === 'text') setPartial((value) => value + chunk.text);
        if (chunk.type === 'tool') {
          const index = activeSteps.findIndex((item) => item.id === chunk.id);
          activeSteps = index < 0 ? [...activeSteps, chunk] : activeSteps.map((item, position) => position === index ? chunk : item);
          if (inflight.current?.requestId === requestId) inflight.current.steps = activeSteps;
          setSteps(activeSteps);
          announceCompletedMutations(requestId, [chunk], announcedMutations.current, (recordId) => announceExternalRecordMutation({ recordId }));
        }
      }, abort.signal, checkpoint?.id, { sujetId: sujet.id, titreSujet: titreDuSujetPourLaNotification(sujet.titre, historyTurns) });
      if (abort.signal.aborted || stoppedRequests.current.has(requestId) || controller.current !== abort) return;
      const finalSteps = [...new Map([...activeSteps, ...(reply.steps ?? [])].map((step) => [step.id, step])).values()];
      announceCompletedMutations(requestId, finalSteps, announcedMutations.current, (recordId) => announceExternalRecordMutation({ recordId }));
      const turnId = checkpoint?.turnId ?? crypto.randomUUID();
      const previousRecords = checkpoint ? historyTurns.find((turn) => turn.id === turnId)?.records ?? [] : [];
      const finalRecords = [...new Map([...previousRecords, ...(reply.records ?? [])].map((record) => [`${record.appId}:${record.doorId}:${record.id}`, record])).values()];
      const answered: TourLia = { id: turnId, role: 'assistant', text: reply.text, steps: finalSteps, records: finalRecords, at: Date.now() };
      onSujetChange((current) => ({ ...current, tours: checkpoint && current.tours.some((turn) => turn.id === turnId)
        ? current.tours.map((turn) => turn.id === turnId ? answered : turn) : [...current.tours, answered] }), 'tour');
      if (!openRef.current) setUnread(liaUnread({ status: reply.status, steps: finalSteps }));
      setResume(reply.status === 'paused' && reply.resumeId
        ? { id: reply.resumeId, requestId, context: requestContext, turnId, messages: history } : undefined);
      setRecovery(undefined);
      setStatus('');
      setPartial('');
      setSteps([]);
    } catch (cause) {
      if (controller.current !== abort) return;
      const interrupted = abort.signal.aborted || stoppedRequests.current.has(requestId);
      const mustReconnect = !interrupted && cause instanceof AskTakiError && cause.reconnect;
      const settled = !interrupted && cause instanceof AskTakiError && cause.settled;
      setReconnect(mustReconnect);
      setRecovery(mustReconnect || settled ? undefined : { requestId, context: requestContext, messages: history,
        turnId: checkpoint?.turnId, resumeId: checkpoint?.id });
      setError(interrupted ? 'Réponse interrompue. Récupérez la réponse pour connaître les actions terminées.'
        : cause instanceof Error ? cause.message : 'Lia ne répond pas pour le moment.');
      if (!openRef.current) setUnread('error');
      setStatus('');
      setPartial('');
    } finally {
      if (controller.current === abort) {
        if (inflight.current?.requestId === requestId) inflight.current = null;
        controller.current = null;
        setPending(false);
        // Le champ redevient actif ICI (`pending` retombe) : le focus se rend apres, succes ou echec, pour ecrire la suite.
        if (openRef.current) requestAnimationFrame(() => textarea.current?.focus());
      }
      stoppedRequests.current.delete(requestId);
    }
  }

  const pastille = ficheDuComposeur(sujet.fiche, ficheOuverte);
  const changerFiche = (): void => {
    if (pending) return;
    onSujetChange((current) => ({ ...current, fiche: ficheOuverte }), 'fiche');
  };
  const pastilleDeFiche = pastille ? <div className={cn(compact ? 'mt-1.5' : 'mb-1.5', 'flex min-w-0 items-center gap-1.5 text-[0.6875rem] leading-4 text-ink-500')}>
        <span className="inline-flex h-[1.125rem] max-w-[14rem] shrink-0 items-center gap-1 truncate rounded-pill border border-accent-tint-border bg-accent-tint px-1.5 font-semibold text-accent-ink" title={pastille.fiche.titre}>
          <IconViewDocumentFiles size={10} /><span className="truncate">{pastille.fiche.titre}</span>
        </span>
        <span className="min-w-0 truncate">{pastille.suivie ? 'Lia répond avec cette fiche sous les yeux' : 'Lia verra cette fiche au premier message'}</span>
        {pastille.suivie && ficheOuverte && (ficheOuverte.recordId !== pastille.fiche.recordId || ficheOuverte.doorId !== pastille.fiche.doorId) && (
          <button type="button" onClick={changerFiche} aria-disabled={pending} title={pending ? 'Attendez la fin de cette réponse.' : `Suivre « ${ficheOuverte.titre} » à la place`}
            className="ml-auto shrink-0 font-semibold text-ink-600 underline-offset-2 hover:underline focus-visible:shadow-ring">changer</button>)}
      </div> : null;
  const latestStep = steps.at(-1);
  const avatar = compact ? 24 : 32;
  const texte = compact ? 'text-[0.8125rem] leading-[1.1875rem]' : 'text-sm leading-[1.3125rem]';

  return <div className="flex min-h-0 min-w-0 flex-1 flex-col">
    <div ref={scrollArea} onScroll={(event) => {
      const area = event.currentTarget;
      userScrolled.current = area.scrollHeight - area.scrollTop - area.clientHeight > 80;
    }} className={cn('taki-scroll-mask min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface', compact ? 'px-3' : 'px-5',
      shadows.hasAbove && 'taki-scroll-mask-top', shadows.hasBelow && 'taki-scroll-mask-bottom')} role="log" aria-label="Conversation avec Lia" aria-live="polite">
      <div ref={shadows.contentRef} className={turns.length === 0 && !pending && !error ? 'h-full' : 'pb-3'}>
        {turns.length === 0 && !pending && !error ? <div className="flex min-h-full flex-col items-center justify-center py-6 text-center">
          <AvatarLia size={48} />
          <div className="mt-3 max-w-xs"><h2 className="text-lg font-semibold leading-6 text-ink-900">Hello, moi c’est Lia :)</h2>
            <p className="mt-1.5 text-sm leading-5 text-ink-500">{unavailable ? 'L’accès IA est désactivé pour cette organisation.' : 'Un sujet par conversation : chercher une info, retrouver une fiche, la modifier.'}</p>
          </div>
        </div> : turns.map((turn, index) => {
          const jour = separateurDeJour(turn.at, turns[index - 1]?.at);
          const outilsResumes = (turn as TourLia & { outilsResumes?: { lectures: number; ecritures: number; echecs: number } }).outilsResumes;
          const question = turn.role === 'user';
          const aDesFiches = !question && liaRecordCards(turn.steps ?? [], turn.records ?? [], turn.text).length > 0;
          return <div key={turn.id}>
            {jour && <div role="separator" data-jour-du-fil className="taki-jour-du-fil">{jour}</div>}
            {/* P13 et P21 : la question a droite dans sa bulle, la reponse de Lia a gauche dans la sienne, sans filet. */}
            <article data-message-moi={question || undefined} className={cn('mt-4 flex items-end gap-2.5', question && 'justify-end')}>
              {!question && <span className="mb-0.5 shrink-0"><AvatarLia size={avatar} /></span>}
              <div className={cn('flex min-w-0 max-w-[min(86%,calc(100%-2.5rem))] flex-col', question ? 'items-end' : 'items-start')}>
                <div className={cn('relative mb-1.5 flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5', question && 'justify-end', compact ? 'text-xs' : 'text-[0.8125rem]')}>
                  <span className="font-semibold text-ink-900">{question ? moi.nom : 'Lia'}</span>
                  {turn.at !== undefined && <time className="text-[0.6875rem] text-ink-500" dateTime={new Date(turn.at).toISOString()}>{heureDuTour(turn.at)}</time>}
                  {!question && <ToolHistory steps={turn.steps ?? []} entete />}
                  {turn.role === 'assistant' && !turn.steps?.length && resumeDesOutilsLia(outilsResumes) !== null && (
                    <span className="rounded-pill border border-border px-1.5 text-[0.625rem] leading-4 text-ink-500">
                      {resumeDesOutilsLia(outilsResumes)}
                    </span>)}
                </div>
                {question ? <p className={cn('taki-bulle taki-bulle-moi taki-bulle-pointe max-w-full whitespace-pre-wrap break-words', texte)}>{turn.text}</p>
                  : <div className={cn('taki-bulle max-w-full', !aDesFiches && 'taki-bulle-pointe', texte)}>
                    <MessageText text={turn.text} records={liaKnownRecords(turn.steps ?? [], turn.records)} onOpenRecord={openRecord} />
                    <PlaceCards steps={turn.steps ?? []} appEntries={appEntries} onOpenPlace={onOpenPlace} />
                    {liaUnresolvedFailure(turn.steps ?? []) && <p className="mt-2 text-xs text-danger-ink">Une action a échoué ou a été refusée. Consultez le détail des outils.</p>}
                  </div>}
                {!question && <RecordCards steps={turn.steps ?? []} records={turn.records} answer={turn.text} appEntries={appEntries} onOpenRecord={openRecord} />}
              </div>
            </article>
          </div>;
        })}
        {(pending || steps.length > 0 || error) && <article className={cn('grid gap-x-3 py-2.5', compact ? 'grid-cols-[1.5rem_minmax(0,1fr)]' : 'grid-cols-[2rem_minmax(0,1fr)]')}>
          <AvatarLia size={avatar} />
          <div className={cn('min-w-0 text-ink-900', texte)}>
            <div className={cn('flex items-baseline gap-2', compact ? 'text-xs' : 'text-[0.8125rem]')}><span className="font-semibold">Lia</span></div>
            {pending && <EtatDeLiaAuTravail etape={latestStep ?? null} depuis={debut} statut={status || undefined}
              detailLong={(secondes) => `C’est plus long que d’habitude (${secondes} s). L’app reste utilisable ; passé deux minutes et demie, Lia fait une pause et dit où elle en est.`} />}
            {!pending && <ToolHistory steps={steps} uncertain />}
            {partial && <div className="mt-1 break-words"><MessageText text={partial} records={liaKnownRecords(steps)} onOpenRecord={openRecord} /></div>}
            {error && <p role="alert" className="mt-2 text-sm leading-5 text-danger-ink">{error}</p>}
            {reconnect && !pending && <button type="button" onClick={() => void signOutTaki()}
              className="mt-3 rounded-sm border border-accent-border bg-accent px-3 py-1.5 text-sm font-semibold text-accent-label hover:bg-accent-hover focus-visible:shadow-ring">Se reconnecter</button>}
            {!pending && <RecordCards steps={steps} answer={error ?? ''} appEntries={appEntries} onOpenRecord={openRecord} />}
            {!pending && <PlaceCards steps={steps} appEntries={appEntries} onOpenPlace={onOpenPlace} />}
            {!pending && (resume || recovery) && <button type="button" onClick={() => void continueRequest()}
              className="mt-3 rounded-sm border border-accent-border bg-accent px-3 py-1.5 text-sm font-semibold text-accent-label hover:bg-accent-hover focus-visible:shadow-ring">
              {resume ? 'Continuer' : 'Récupérer la réponse'}
            </button>}
          </div>
        </article>}
        {recordNotice && <p role="status" className="mt-2 text-xs leading-4 text-ink-500">{recordNotice}</p>}
      </div>
    </div>
    <form onSubmit={(event) => void submit(event)} data-composeur-conversation className={cn('shrink-0 bg-surface', compact ? 'px-2.5 pb-2.5 pt-1' : 'px-5 pb-3 pt-1.5')}>
{/* La pastille de la fiche : au-dessus du champ dans le centre agrandi (hub-a), en dessous dans la fenetre (hub-d). */}
      {!compact && pastilleDeFiche}
      <div ref={champ} className="contents">
        <BarreDeSaisie value={draft} onChange={setDraft} entrees={AUCUNE_MENTION} onEnvoyer={() => void submit()}
          peutEnvoyer={draft.trim() !== '' && !unavailable}
          placeholder={lectureSeule ?? (unavailable ? 'Assistant désactivé' : 'Écrivez à Lia…')} ariaLabel="Votre message à Lia"
          disabled={pending || unavailable} maxLength={4_000} lignesMax={compact ? 8 : 10}
          action={pending ? <button key="stop" type="button" onClick={stop} aria-label="Arrêter la réponse" title="Arrêter la réponse" className="relative mb-[1px] flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink-900 text-surface focus-visible:shadow-ring after:absolute after:-inset-1.5 after:content-['']"><span className="h-2.5 w-2.5 rounded-[2px] bg-current" /></button> : undefined} />
      </div>
      {compact && pastilleDeFiche}
      {!compact && <p className="mt-1.5 text-[0.6875rem] leading-4 text-ink-400">Entrée envoie · Maj+Entrée saute une ligne</p>}
    </form>
  </div>;
}

function ToolHistory({ steps, uncertain = false, entete = false }: { steps: Step[]; uncertain?: boolean; entete?: boolean }): JSX.Element | null {
  const panneau = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!entete) return;
    const fermer = (event: Event): void => {
      if (event.target instanceof Node && panneau.current?.open && !panneau.current.contains(event.target)) panneau.current.open = false;
    };
    // Capturer avant les gestionnaires des autres boutons, meme s'ils arretent l'evenement.
    document.addEventListener('pointerdown', fermer, true);
    document.addEventListener('focusin', fermer, true);
    return () => {
      document.removeEventListener('pointerdown', fermer, true);
      document.removeEventListener('focusin', fermer, true);
    };
  }, [entete]);
  if (steps.length === 0) return null;
  return <details ref={panneau} onKeyDown={(event) => {
    if (event.key === 'Escape' && panneau.current?.open) {
      panneau.current.open = false;
      panneau.current.querySelector('summary')?.focus();
      event.stopPropagation();
    }
  }} className={cn('group min-w-0 max-w-full text-xs leading-4 text-ink-500', !entete && 'mb-1.5')}>
    <summary className={cn('flex w-fit cursor-pointer list-none items-center gap-1.5 rounded-sm outline-none hover:text-ink-900 focus-visible:shadow-ring [&::-webkit-details-marker]:hidden', entete ? 'min-h-5 text-[0.6875rem]' : 'min-h-7')}>
      {!entete && <span aria-hidden="true" className="flex items-center pl-1">{steps.slice(-4).map((step, index) => <span key={step.id} style={{ zIndex: 4 - index }} className={cn('relative -ml-1 flex h-[1.375rem] w-[1.375rem] items-center justify-center rounded-full border-2 border-surface bg-surface-3', index > 0 && '-ml-2')}><ToolIcon kind={step.kind} size={11} /></span>)}</span>}
      <span>{steps.length} {steps.length === 1 ? 'appel d’outil' : 'appels d’outils'}</span><IconChevronDown size={13} className="transition-transform group-open:rotate-180 motion-reduce:transition-none" />
    </summary>
    <ol className={entete ? 'absolute left-0 top-full z-20 mt-1.5 max-h-60 w-80 max-w-full overflow-y-auto rounded-xl border border-border bg-surface p-2 shadow-pop' : 'mt-1 border-b border-border pb-2'}>{steps.map((step) => <li key={step.id} className={cn('flex min-h-8 items-start gap-2 py-1.5', entete && 'px-2 text-[0.6875rem]')}>
      <span aria-hidden="true" className="mt-0.5"><ToolIcon kind={step.kind} size={14} /></span>
      <span className="min-w-0 flex-1"><span className="block font-medium text-ink-900">{step.label}</span>
        {step.detail && <span className="block break-words text-ink-500">{step.detail}</span>}
        {uncertain && step.state === 'running' && <span className="block">État à vérifier</span>}
      </span>
      <span className={step.state === 'error' ? 'text-danger-ink' : 'text-accent-soft-ink'} aria-label={step.state === 'running' ? 'État à vérifier' : step.state === 'error' ? step.outcome === 'denied' ? 'Refusé' : 'Échec' : 'Fait'}>{step.state === 'done' ? <IconCheck size={13} /> : step.state === 'error' ? <IconClose size={13} /> : '?'}</span>
    </li>)}</ol>
  </details>;
}

/** Les fiches lues, creees ou modifiees par un tour : le rendu partage de @taki/ui (le meme dans Canaux). */
function RecordCards({ steps, records, answer, appEntries, onOpenRecord }: { steps: Step[]; records?: RecordLink[]; answer?: string; appEntries: readonly ShellAppEntry[]; onOpenRecord: (record: RecordLink) => void }): JSX.Element | null {
  return <CartesDeFichesLia steps={steps} records={records ?? []} {...(answer !== undefined ? { answer } : {})}
    iconeDApp={(appId) => appEntries.find((entry) => entry.id === appId)?.icon} onOpenRecord={onOpenRecord} />;
}

/** Les apps et portes que Lia a proposé d'ouvrir : une carte chacune, la dernière demande gagne. */
function PlaceCards({ steps, appEntries, onOpenPlace }: { steps: Step[]; appEntries: readonly ShellAppEntry[]; onOpenPlace: ConversationLiaProps['onOpenPlace'] }): JSX.Element | null {
  const places = [...new Map(steps.filter((step) => step.state === 'done' && step.place)
    .map((step) => [`${step.place!.appId}:${step.place!.doorId ?? ''}`, step.place!] as const)).values()];
  if (places.length === 0) return null;
  const nature = (place: PlaceLink) => place.nature === 'canvas' ? 'Surface Canvas' : place.nature === 'outil' ? 'Outil interne' : 'App';
  return <div className="mt-2 flex flex-wrap gap-1.5">{places.map((place) => {
    const title = place.doorLabel ?? place.appName;
    return <button key={`${place.appId}:${place.doorId ?? ''}`} type="button" onClick={() => onOpenPlace(place)} aria-label={`Ouvrir ${title}`}
      className="flex min-h-9 max-w-full items-center gap-2 rounded-sm border border-accent-tint-border bg-accent-tint px-2 py-1 text-left hover:bg-accent-soft focus-visible:shadow-ring">
      <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-xs bg-surface text-accent-soft-ink [&>svg]:h-3.5 [&>svg]:w-3.5">{appEntries.find((entry) => entry.id === place.appId)?.icon ?? <IconModule size={14} />}</span>
      <span className="min-w-0 truncate text-[0.8125rem] font-semibold leading-5 text-ink-900">{title}</span>
      <span className="shrink-0 text-[0.6875rem] leading-4 text-ink-500">{place.doorLabel ? `${place.appName} · porte` : nature(place)} · ouvrir</span>
    </button>;
  })}</div>;
}
